import { useState, useEffect, useRef } from 'react';

const checkItems = [
  'AかBか迷い続けて、自分ではもう決められないと感じる',
  '「あのときどうしたらよかったんだろう」と考え続けてしまう',
  'いろいろ学んできたけど、結局まだ楽になってない',
  '自分がどうしたいのか、本当はよくわからない',
  '誰かに相談したいけど、「聞くのは迷惑かも」と思ってしまう',
  '誰かのためには頑張れるのに、自分のことになると罪悪感が出る',
  '人と一緒にいると過度に緊張する、見られている感じがする',
  '変わりたいと思うのに、変わることに抵抗がある',
];

export default function SelfCheckSection() {
  const [checked, setChecked] = useState<boolean[]>(Array(checkItems.length).fill(false));
  const [resultVisible, setResultVisible] = useState(false);
  const [resultRendered, setResultRendered] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  // Fade-in on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const toggle = (i: number) => {
    const next = [...checked];
    next[i] = !next[i];
    setChecked(next);

    const anyChecked = next.some(Boolean);
    if (anyChecked && !resultRendered) {
      setResultRendered(true);
      setTimeout(() => setResultVisible(true), 50);
    } else if (anyChecked) {
      setResultVisible(true);
    } else {
      setResultVisible(false);
      setTimeout(() => setResultRendered(false), 600);
    }
  };

  const scrollToWhy = () => {
    const el = document.getElementById('why-section');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="self-check" className="w-full bg-[#FAF8F5] py-24 px-4">
      <div
        ref={sectionRef}
        className="max-w-2xl mx-auto opacity-0 translate-y-8 transition-all duration-1000"
      >
        <p className="text-center font-sans text-[#8FA68B] text-xs tracking-[0.4em] mb-6 uppercase">
          self check
        </p>
        <h2 className="font-serif text-3xl md:text-4xl text-[#3A3530] text-center leading-snug mb-16">
          こんなこと、ありませんか？
        </h2>

        {/* Checklist */}
        <ul className="space-y-3">
          {checkItems.map((item, i) => (
            <li key={i}>
              <button
                onClick={() => toggle(i)}
                className="cursor-pointer w-full flex items-start gap-4 p-5 rounded-lg text-left transition-all duration-300"
                style={{
                  backgroundColor: checked[i] ? 'rgba(143, 166, 139, 0.12)' : 'white',
                  border: `1px solid ${checked[i] ? '#8FA68B' : '#E8E2DA'}`,
                }}
              >
                {/* Round checkbox */}
                <div
                  className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 transition-all duration-300 flex items-center justify-center"
                  style={{
                    borderColor: checked[i] ? '#8FA68B' : '#C2A882',
                    backgroundColor: checked[i] ? '#8FA68B' : 'transparent',
                  }}
                >
                  {checked[i] && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path d="M1 4l2.5 2.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <span
                  className="font-sans text-sm leading-relaxed tracking-wide transition-colors duration-300"
                  style={{ color: checked[i] ? '#3A3530' : '#6B6460' }}
                >
                  {item}
                </span>
              </button>
            </li>
          ))}
        </ul>

        {/* Result section */}
        {resultRendered && (
          <div
            ref={resultRef}
            className="mt-10 transition-all duration-600"
            style={{
              opacity: resultVisible ? 1 : 0,
              transform: resultVisible ? 'translateY(0)' : 'translateY(16px)',
              transition: 'opacity 0.6s ease, transform 0.6s ease',
            }}
          >
            {/* Box 1 — Beige background */}
            <div
              className="rounded-lg px-8 py-8 mb-5 text-center"
              style={{ backgroundColor: 'rgba(194, 168, 130, 0.15)', border: '1px solid rgba(194, 168, 130, 0.35)' }}
            >
              <p className="font-serif text-[#3A3530] text-base md:text-lg leading-loose">
                一つでも心に引っかかったものがあったなら——
              </p>
              <p className="font-sans text-[#6B6460] text-sm leading-loose mt-3">
                あなたの中に問題があるわけではないんです。<br />
                その違和感には、心の仕組みで見てみると<br />
                ちゃんと理由があります。
              </p>
            </div>

            {/* Box 2 — White card */}
            <div
              className="rounded-lg px-8 py-8 bg-white"
              style={{ border: '1px solid #EDE7DC' }}
            >
              <p className="font-serif text-[#3A3530] text-base md:text-lg leading-snug text-center mb-7">
                実は、あなたがチェックしたこと。<br />
                バラバラに見えて、全部つながっています。
              </p>

              <ul className="space-y-3 mb-5 pl-2">
                {[
                  '考え続けて決められないのも',
                  '自分がどうしたいかわからなくなるのも',
                  '誰かに頼ることに罪悪感を感じるのも',
                ].map((pt, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: '#8FA68B' }}
                    />
                    <span className="font-sans text-[#6B6460] text-sm">{pt}</span>
                  </li>
                ))}
              </ul>

              <p
                className="font-sans text-center text-[#8FA68B] text-lg tracking-widest mb-5"
              >
                ···
              </p>

              <p className="font-sans text-[#6B6460] text-sm leading-loose text-center mb-7">
                どれも、あなたの心の中で<br />
                あなたを守ろうとする仕組みが<br />
                静かに動いているだけなのです。<br /><br />
                <span className="font-serif text-[#3A3530] text-base">
                  その仕組みには、ちゃんと理由があります。
                </span>
              </p>

              <div className="text-center">
                <button
                  onClick={scrollToWhy}
                  className="cursor-pointer font-sans text-[#8FA68B] text-sm tracking-wide hover:text-[#6B8A67] transition-colors duration-200 underline underline-offset-4"
                  style={{ textDecorationColor: 'rgba(143, 166, 139, 0.5)' }}
                >
                  その理由を、少しだけ見てみる ↓
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
